import api from './api';

export interface Provenance {
  page: number;
  char_start: number;
  char_end: number;
  bbox: number[];
  source_snippet?: string | null;
}

export interface ExtractedField {
  field_key: string;
  value: string;
  normalized_value?: any;
  confidence: number;
  stream: string;
  model: string;
  grounded: boolean;
  provenance: Provenance;
}

export interface StageResult {
  stage: string;
  stream?: string | null;
  status: string;
  retries: number;
  error?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  metrics: any;
}

export interface GuardrailCheck {
  key: string;
  name: string;
  severity: 'violation' | 'advisory';
  passed: boolean;
  detail: string;
  evidence: string[];
}

export interface GuardrailReport {
  passed: boolean;
  violations: number;
  advisories?: number;
  checks: GuardrailCheck[];
}

export interface MarketingBrief {
  topic?: string;
  goal?: string;
  inputs?: string;
  channel?: string;
  content_persona?: string;
  tone?: string;
  brand_voice?: string;
  target_audience?: string;
  length?: string;
}

// ---- IT / Jira agent ----
export interface JiraProposal {
  issue_key: string;
  key?: string;
  summary?: string;
  triage_summary?: string;
  status?: string;
  status_category?: string;
  priority?: string;
  proposed_priority?: string;
  issue_type?: string;
  assignee?: string | null;
  assignee_account_id?: string | null;
  duedate?: string | null;
  age_days?: number;
  overdue?: boolean;
  spillover?: boolean;
  late?: boolean;
  late_reason?: string;
  category?: string;
  action?: string;               // comment | transition | assign | none
  comment?: string;
  transition_target?: string;
  reason?: string;
  blocked?: boolean;
  blocked_reason?: string;
  requires_approval?: boolean;
  degraded?: boolean;
}

export interface JiraConnectionStatus {
  site: string;
  email: string;
  token_configured: boolean;
  token_preview: string;
  configured: boolean;
}

export interface JiraStart {
  project_key?: string;
  jql?: string;
  spillover_days?: number;
  max_tickets?: number;
}

export interface JiraDecision {
  issue_key: string;
  action: string;
  approved: boolean;
  comment?: string;
  transition?: string;
  assignee_account_id?: string;
}

export interface PipelineRun {
  id: string;
  agent_key?: string;
  document_id: string;
  document_filename: string;
  brief?: MarketingBrief & Record<string, any>;
  result?: {
    content?: string;
    model?: string;
    degraded?: boolean;
    approved?: boolean;
    edited?: boolean;
    guardrail_report?: GuardrailReport;
    // jira agent
    tickets?: JiraProposal[];
    proposals?: JiraProposal[];
    counts?: { total?: number; late?: number };
    apply_results?: { issue_key?: string; action?: string; status?: string; detail?: string }[];
    apply_counts?: { applied?: number; skipped?: number; errored?: number };
    jql?: string;
    project_key?: string;
  };
  schema_version_id?: string | null;
  orchestrator_run_id?: string;
  status: string;
  streams: string[];
  hitl_gate: any;
  export_uri?: string | null;
  created_at: string;
  updated_at: string;
  stages: StageResult[];
  extracted_fields?: ExtractedField[];
}

export interface RunListResponse {
  runs: PipelineRun[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface Correction {
  field_key: string;
  corrected_value: string;
  source_snippet?: string;
  page?: number;
}

// The agent a run belongs to. Defaults to the Doc agent so existing callers are unchanged.
const DEFAULT_AGENT = 'doc';

class PipelineService {
  async listRuns(page = 1, per_page = 20, statusFilter?: string, agentKey: string = DEFAULT_AGENT): Promise<RunListResponse> {
    const params = new URLSearchParams({ page: String(page), per_page: String(per_page), agent_key: agentKey });
    if (statusFilter) params.append('status_filter', statusFilter);
    return (await api.get<RunListResponse>(`/pipeline/runs?${params}`)).data;
  }

  async getRun(id: string): Promise<{ run: PipelineRun }> {
    return (await api.get(`/pipeline/runs/${id}`)).data;
  }

  async getGate(id: string): Promise<{ status: string; gate: any }> {
    return (await api.get(`/pipeline/runs/${id}/gate`)).data;
  }

  async startRun(documentId: string, streams?: string[], agentKey: string = DEFAULT_AGENT): Promise<{ run: PipelineRun }> {
    return (await api.post(`/pipeline/runs?agent_key=${encodeURIComponent(agentKey)}`,
      { document_id: documentId, streams })).data;
  }

  async resumeRun(id: string, corrections: Correction[], override = false): Promise<any> {
    return (await api.post(`/pipeline/runs/${id}/resume`, { corrections, override })).data;
  }

  // ---- marketing agent (brief-driven runs) ----
  async startMarketingRun(brief: MarketingBrief): Promise<{ run: PipelineRun }> {
    return (await api.post('/pipeline/marketing/runs', brief)).data;
  }

  async resumeMarketingRun(id: string, content?: string, approved = true): Promise<any> {
    return (await api.post(`/pipeline/marketing/runs/${id}/resume`, { content, approved })).data;
  }

  // ---- IT / Jira agent ----
  async getJiraConnection(): Promise<JiraConnectionStatus> {
    return (await api.get('/pipeline/jira/connection')).data;
  }

  async setJiraConnection(conn: { site?: string; email?: string; token?: string }): Promise<JiraConnectionStatus> {
    return (await api.put('/pipeline/jira/connection', conn)).data;
  }

  async testJiraConnection(): Promise<{ ok: boolean; account?: any; error?: string; status?: number }> {
    return (await api.get('/pipeline/jira/connection/test')).data;
  }

  async startJiraRun(start: JiraStart): Promise<{ run: PipelineRun }> {
    return (await api.post('/pipeline/jira/runs', start)).data;
  }

  async resumeJiraRun(id: string, decisions: JiraDecision[]): Promise<any> {
    return (await api.post(`/pipeline/jira/runs/${id}/resume`, { decisions })).data;
  }

  async agentChat(agentKey: string, messages: { role: string; content: string }[], runId?: string):
    Promise<{ reply: string; grounded: { source: string; items: number; model: string | null } }> {
    return (await api.post(`/pipeline/${agentKey}/chat`, { messages, run_id: runId })).data;
  }

  async cancelRun(id: string): Promise<any> {
    return (await api.post(`/pipeline/runs/${id}/cancel`, {})).data;
  }

  async rerunFromStage(id: string, fromStage?: string): Promise<any> {
    return (await api.post(`/pipeline/runs/${id}/rerun`, { from_stage: fromStage })).data;
  }

  async rerunStream(id: string, stream: string): Promise<any> {
    return (await api.post(`/pipeline/runs/${id}/rerun-stream`, { stream })).data;
  }

  async downloadExport(id: string): Promise<string> {
    return (await api.get(`/pipeline/runs/${id}/export`, { responseType: 'text' })).data;
  }

  // ---- metrics ----
  async summary(): Promise<any> { return (await api.get('/metrics/summary')).data; }
  async streamComparison(): Promise<any> { return (await api.get('/metrics/stream-comparison')).data; }
  async accuracyTrend(): Promise<any> { return (await api.get('/metrics/accuracy-trend')).data; }
  async cost(): Promise<any> { return (await api.get('/metrics/cost')).data; }
  async energy(): Promise<any> { return (await api.get('/metrics/energy')).data; }
}

export const pipelineService = new PipelineService();
export default pipelineService;
